/**
 * switchWorkspace — clears workspace + project state, invalidates all
 * fetch cache queries (stale on next read), loads the new workspace.
 */

import { setActiveWorkspacePointer } from '../activeWorkspacePointer.js'
import { syncWorkspaceUrl } from '../workspaceUrlSync.js'
import { setActiveWorkspaceRecord, stampWorkspaceShape } from '../activeWorkspaceRecord.js'

/**
 * @param {object} state           - DOMQL root state
 * @param {object} sdk             - SDK instance (must have workspace.getWorkspace)
 * @param {object} queryClient     - @symbo.ls/fetch queryClient
 * @param {string} newWorkspaceId  - target workspace ID
 */
export const switchWorkspace = async (state, sdk, queryClient, newWorkspaceId) => {
  // Self-contained locals — see switchOrg.js's identical note (same fix,
  // same collision class: WORKSPACE_KEYS/PROJECT_KEYS were separately
  // module-scope in both files with matching values, a latent frank-rename
  // collision waiting to diverge).
  //
  // Scope THIS tab's SDK to the new workspace first — the client-only
  // `updateContext` bootShell and the auth tick use (no Mongo write: the tab
  // that made the switch already persisted it). `_workspaceScope()` reads
  // `_context.activeWorkspaceId` BEFORE the storage pointer, so without this
  // every scope-derived call — ticket get / create / update / comments and,
  // since sdk 3.14.794, the attachment upload — kept targeting the OLD
  // workspace until the next auth tick, while the state below already showed
  // the new one. Before the query invalidation too, so the refetches it
  // triggers read the new workspace.
  sdk.updateContext?.({ activeWorkspaceId: newWorkspaceId })
  for (const k of ['workspace', 'workspaceMembers', 'workspaceIntegrations', 'workspaceBilling']) {
    state[k] = null
  }
  for (const k of ['project', 'projectComponents', 'projectPages', 'projectState']) {
    state[k] = null
  }

  // Invalidate all workspace-scoped fetch cache entries.
  // Cache keys include workspace ids in params; nuking by 'workspace' prefix
  // would over-evict, so we use queryClient.invalidateQueries() to mark all
  // queries stale — they'll refetch on next read.
  queryClient.invalidateQueries()

  // FLAT SDK method — `sdk.workspace` is NOT a property (services live in
  // the internal Map behind getService; SERVICE_METHODS proxies expose
  // sdk.getWorkspace). The old nested `sdk.workspace.getWorkspace(...)`
  // threw a TypeError mid-function on EVERY call, so the pointer write and
  // the URL sync below never ran — the cross-tab workspace-switch
  // convergence path was dead outright, and reloads snapped back to the
  // org HQ (2026-07-30, part of the GreenIG "widgets rearranged /
  // data not loading" investigation).
  const res = await sdk.getWorkspace(newWorkspaceId)
  // Same envelope unwrap every other getWorkspace consumer uses
  // (getWorkspaceCached / moduleConsole): the SDK may return {data}.
  const ws = (res && res.data) || res || null
  // sdk.getWorkspace always returns the full record — stamp it so a racing
  // claims tick (auth.js's subscribeAuthSession) can never downgrade it back
  // to a list entry for this id. See tickets/spec-workspace-record-shape.md.
  setActiveWorkspaceRecord(state, ws ? stampWorkspaceShape(ws, 'full', { clone: false }) : null, {
    reason: 'switchWorkspace',
    sdk
  })
  setActiveWorkspacePointer(newWorkspaceId)
  // Name the new workspace in the URL. Without this the address bar still reads
  // `/w/<org-slug>`, which resolves to the org's HQ on reload — so switching to
  // a second workspace looked like it worked until you refreshed.
  syncWorkspaceUrl(state)
}
